import { createAsyncThunk } from '@reduxjs/toolkit';

import { clearCourses } from 'Courses/data';
import { urls } from 'Router/urls';
import { disconnectSignalR } from 'SignalR';
import { clearCurrentUserInfo, getCurrentUserInfo } from 'User/data';

import { authApi } from '@/Api';
import type {
  IChangePasswordRequest,
  IConfirmEmailAndActivateAccountRequest,
  IForgotPasswordRequest,
  ILogInAndJoinToCourseRequest,
  ILogInOrSignUpViaGoogleRequest,
  ILogInOrSignUpViaLinkedInRequest,
  ILogInRequest,
  IRegisterAndJoinToCourseRequest,
  IRegisterRequest,
  IResetPasswordRequest,
  Result
} from '@/libs/models';

async function getCurrentUserInfoAndRedirectToHomePageAfterSuccess(
  callback: Promise<void>,
  thunkApi: any
) {
  try {
    await callback;

    thunkApi.dispatch(getCurrentUserInfo());
    return { data: {}, shouldNavigateTo: urls.homePage };
  } catch (exception: any) {
    return thunkApi.rejectWithValue(exception.response?.data ?? exception.message);
  }
}

async function getCurrentUserInfoAndRedirectToHomePageAfterResultSuccess<T>(
  callback: Promise<Result<T>>,
  thunkApi: any
) {
  try {
    const result = await callback;

    if (result.isSuccess) {
      thunkApi.dispatch(getCurrentUserInfo());
      return { data: result.data, shouldNavigateTo: urls.homePage };
    }

    return thunkApi.rejectWithValue(result.error ?? 'Operation failed');
  } catch (exception: any) {
    return thunkApi.rejectWithValue(exception.response?.data ?? exception.message);
  }
}

async function handleError(callback: Promise<void>, thunkApi: any) {
  try {
    await callback;
    return { data: {} };
  } catch (exception: any) {
    return thunkApi.rejectWithValue(exception.response?.data ?? exception.message);
  }
}

export const logInOrSignUpViaGoogle = createAsyncThunk(
  'auth/logInOrSignUpViaGoogle',
  async (request: ILogInOrSignUpViaGoogleRequest, thunkApi) =>
    getCurrentUserInfoAndRedirectToHomePageAfterSuccess(
      authApi.loginOrSignUpViaGoogle(request),
      thunkApi
    )
);

export const logInOrSignUpViaLinkedIn = createAsyncThunk(
  'auth/logInOrSignUpViaLinkedIn',
  async (request: ILogInOrSignUpViaLinkedInRequest, thunkApi) =>
    getCurrentUserInfoAndRedirectToHomePageAfterSuccess(
      authApi.loginOrSignUpViaLinkedIn(request),
      thunkApi
    )
);

export const login = createAsyncThunk('auth/login', async (request: ILogInRequest, thunkApi) =>
  getCurrentUserInfoAndRedirectToHomePageAfterSuccess(authApi.login(request), thunkApi)
);

export const loginAndJoinToCourse = createAsyncThunk(
  'auth/loginAndJoinToCourse',
  async (request: ILogInAndJoinToCourseRequest, thunkApi) =>
    getCurrentUserInfoAndRedirectToHomePageAfterResultSuccess(
      authApi.loginAndJoinCourse(request),
      thunkApi
    )
);

export const register = createAsyncThunk(
  'auth/register',
  async (request: IRegisterRequest, thunkApi) => {
    request.origin = window.location.origin + import.meta.env.BASE_URL;
    return handleError(authApi.register(request), thunkApi);
  }
);

export const confirmEmailAndActivateAccount = createAsyncThunk(
  'auth/confirmEmailAndActivateAccount',
  async (code: string, thunkApi) => {
    try {
      await authApi.confirmEmailAndActivateAccount({
        code
      } as IConfirmEmailAndActivateAccountRequest);
      return { data: {} };
    } catch (exception: any) {
      return thunkApi.rejectWithValue(exception.response?.data ?? exception.message);
    }
  }
);

export const registerAndJoinToCourse = createAsyncThunk(
  'auth/registerAndJoinToCourse',
  async (request: IRegisterAndJoinToCourseRequest, thunkApi) =>
    getCurrentUserInfoAndRedirectToHomePageAfterResultSuccess(
      authApi.registerAndJoinCourse(request),
      thunkApi
    )
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (request: IForgotPasswordRequest, thunkApi) => {
    request.origin = window.location.origin + import.meta.env.BASE_URL;
    return handleError(authApi.forgotPassword(request), thunkApi);
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (request: IResetPasswordRequest, thunkApi) => {
    try {
      await authApi.resetPassword(request);
      return {
        data: {},
        shouldNavigateTo: urls.loginPage,
        navigationState: { isAfterResetPassword: true }
      };
    } catch (exception: any) {
      return thunkApi.rejectWithValue(exception.response?.data ?? exception.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (request: IChangePasswordRequest, thunkApi) =>
    handleError(authApi.changePassword(request), thunkApi)
);

export const logout = createAsyncThunk('auth/logout', async (_, thunkApi) => {
  await authApi.logout();
  thunkApi.dispatch(clearCurrentUserInfo());
  thunkApi.dispatch(clearCourses());
  disconnectSignalR();
});

export {
  clearChangePasswordError,
  clearForgotPasswordError,
  clearIsChangePasswordSuccess,
  clearIsForgotPasswordSuccess,
  clearIsRegistrationSuccess,
  clearLoginError,
  clearRegistrationError,
  clearResetPasswordError,
  setLoggedIn
} from './slice';
